import type { RichTableBlockModel } from '@blocksuite/affine-model';
import { PlusIcon } from '@blocksuite/icons/lit';
import { BlockComponent } from '@blocksuite/std';
import { signal } from '@preact/signals-core';
import { html } from 'lit';

import { insertColumn, insertRow } from './commands/table-ops.js';
import {
  addBothButtonStyles,
  addColumnButtonStyles,
  addRowButtonStyles,
  columnReorderMarkerStyles,
  DEFAULT_COLUMN_WIDTH,
  MIN_COLUMN_WIDTH,
  richTableGridStyles,
  richTableHostStyles,
  richTableWrapperStyles,
  rowReorderMarkerStyles,
} from './styles.js';

export class RichTableBlockComponent extends BlockComponent<RichTableBlockModel> {
  /**
   * Which column/row the pointer is over, so a cell can decide whether to show
   * its handle. Tracked here rather than per-cell because hovering *any* cell
   * in a column should reveal that column's handle, not just the first row's.
   */
  readonly hoverColumn$ = signal<number | null>(null);
  readonly hoverRow$ = signal<number | null>(null);

  /**
   * Reorder state, set by a row/column handle while its drag is in flight.
   * `from` dims the moving row or column, `to` places the insertion marker.
   */
  readonly reorderColumnFrom$ = signal<number | null>(null);
  readonly reorderColumnTo$ = signal<number | null>(null);
  readonly reorderRowFrom$ = signal<number | null>(null);
  readonly reorderRowTo$ = signal<number | null>(null);

  private get _reordering() {
    return (
      this.reorderColumnFrom$.peek() !== null ||
      this.reorderRowFrom$.peek() !== null
    );
  }

  private readonly _onPointerMove = (event: PointerEvent) => {
    // A reorder drag owns the pointer; hover updates would fight its marker.
    if (this._reordering) return;
    const cell = (event.target as HTMLElement | null)?.closest(
      'affine-rich-table-cell'
    );
    if (!cell || !this.contains(cell)) {
      this._clearHover();
      return;
    }
    const row = cell.parentElement?.closest('affine-rich-table-row');
    this.hoverColumn$.value = cell.parentElement
      ? [...cell.parentElement.children].indexOf(cell)
      : null;
    this.hoverRow$.value = row
      ? [...(row.parentElement?.children ?? [])].indexOf(row)
      : null;
  };

  private readonly _clearHover = () => {
    if (this._reordering) return;
    this.hoverColumn$.value = null;
    this.hoverRow$.value = null;
  };

  /**
   * Heals tables written before column widths became pixels.
   *
   * The first cut stored flex ratios — values like 1 or 0.4 — which as pixels
   * would render as slivers. Anything below the minimum column width can only
   * be a ratio, so the row is reset to defaults.
   */
  private _migrateLegacyRatios() {
    const widths = this.model.props.columnWidths;
    if (this.store.readonly) return;
    if (widths.length === 0) return;
    if (widths.every(width => width >= MIN_COLUMN_WIDTH)) return;

    this.store.transact(() => {
      this.model.props.columnWidths = widths.map(() => DEFAULT_COLUMN_WIDTH);
    }, false);
  }

  override connectedCallback() {
    super.connectedCallback();
    // Shadowless elements get no `:host`, so the host rule is a class.
    this.classList.add(richTableHostStyles);
    this._migrateLegacyRatios();
    this.addEventListener('pointermove', this._onPointerMove);
    this.addEventListener('pointerleave', this._clearHover);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointermove', this._onPointerMove);
    this.removeEventListener('pointerleave', this._clearHover);
  }

  private _addButton(testId: string, className: string, run: () => void) {
    return html`<div
      data-testid=${testId}
      class=${className}
      @click=${(event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.store.readonly) return;
        run();
      }}
    >
      ${PlusIcon()}
    </div>`;
  }

  /** Left edge of the insertion marker, in wrapper coordinates. */
  private _columnMarkerOffset(index: number) {
    const widths = this.model.props.columnWidths;
    return widths.slice(0, index).reduce((sum, width) => sum + width, 0);
  }

  private _renderReorderMarkers() {
    const columnTo = this.reorderColumnTo$.value;
    const columnFrom = this.reorderColumnFrom$.value;
    const rowTo = this.reorderRowTo$.value;
    const rowFrom = this.reorderRowFrom$.value;

    const markers = [];

    if (columnTo !== null && columnFrom !== null && columnTo !== columnFrom) {
      // Dropping to the right lands after the target column.
      const edge = columnTo > columnFrom ? columnTo + 1 : columnTo;
      markers.push(
        html`<div
          data-testid="rich-table-column-marker"
          class=${columnReorderMarkerStyles}
          style="left: ${this._columnMarkerOffset(edge)}px"
        ></div>`
      );
    }

    if (rowTo !== null && rowFrom !== null && rowTo !== rowFrom) {
      // Rows are `display: contents` and so have no box of their own; measure
      // the first cell of the row instead.
      const rows = [...this.querySelectorAll('affine-rich-table-row')];
      const edge = rowTo > rowFrom ? rowTo + 1 : rowTo;
      const gridTop =
        this.querySelector('.affine-rich-table-grid')?.getBoundingClientRect()
          .top ?? 0;
      const anchor = rows[Math.min(edge, rows.length - 1)]?.firstElementChild;
      const rect = anchor?.getBoundingClientRect();
      const top = rect
        ? (edge >= rows.length ? rect.bottom : rect.top) - gridTop
        : 0;
      markers.push(
        html`<div
          data-testid="rich-table-row-marker"
          class=${rowReorderMarkerStyles}
          style="top: ${top}px"
        ></div>`
      );
    }

    return markers;
  }

  override renderBlock() {
    // Reading the signal keeps widths reactive to undo/redo and remote edits.
    const widths = this.model.props.columnWidths$.value;
    const template = widths.map(width => `${width}px`).join(' ');

    return html`
      <div class="affine-rich-table-wrapper ${richTableWrapperStyles}">
        <div
          data-drag-bounds
          class="affine-rich-table-grid ${richTableGridStyles}"
          style="grid-template-columns: ${template}"
        >
          ${this.renderChildren(this.model)}
        </div>
        ${this._addButton('rich-table-add-column', addColumnButtonStyles, () =>
          insertColumn(this.store, this.model)
        )}
        ${this._addButton('rich-table-add-row', addRowButtonStyles, () =>
          insertRow(this.store, this.model)
        )}
        ${this._addButton('rich-table-add-both', addBothButtonStyles, () => {
          insertRow(this.store, this.model);
          insertColumn(this.store, this.model);
        })}
        ${this._renderReorderMarkers()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-rich-table': RichTableBlockComponent;
  }
}
